import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Badge from "@/components/base/Badge";
import {
  getReminderHistory,
  REMINDER_CHANNEL_LABEL,
  type Reminder,
  type UnpaidRow,
} from "@/services/reminderService";
import { formatDate, formatMoney } from "@/utils/format";

interface Props {
  row: UnpaidRow | null;
  onClose: () => void;
}

/** Historique des relances d'un élève (WhatsApp et avis imprimés), la plus récente d'abord. */
export default function ReminderHistoryModal({ row, onClose }: Props) {
  const [history, setHistory] = useState<Reminder[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!row) return;
    setHistory(null);
    setError(null);
    getReminderHistory(row.studentId)
      .then(setHistory)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Historique indisponible."));
  }, [row]);

  return (
    <Modal
      open={row !== null}
      onClose={onClose}
      title="Historique des relances"
      subtitle={row ? `${row.firstName} ${row.lastName} — ${row.className}` : undefined}
      icon="ri-history-line"
      size="md"
    >
      {error && <p className="text-sm text-accent-700">{error}</p>}
      {!error && history === null && <p className="text-sm text-foreground-500">Chargement...</p>}
      {history?.length === 0 && (
        <p className="rounded-md border border-dashed border-background-300 py-6 text-center text-sm text-foreground-500">
          Aucune relance pour cet élève.
        </p>
      )}
      {history && history.length > 0 && (
        <ul className="divide-y divide-background-200 rounded-md border border-background-200">
          {history.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
              <div className="flex items-center gap-3">
                <span
                  className={`w-8 h-8 rounded-md flex items-center justify-center ${
                    r.channel === "WHATSAPP" ? "bg-primary-100 text-primary-700" : "bg-secondary-100 text-secondary-700"
                  }`}
                >
                  <i className={r.channel === "WHATSAPP" ? "ri-whatsapp-line" : "ri-printer-line"}></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">
                    {REMINDER_CHANNEL_LABEL[r.channel]} — {formatMoney(r.amount)}
                  </p>
                  <p className="text-xs text-foreground-500">
                    Le {formatDate(r.createdAt)} par {r.createdByName}
                  </p>
                </div>
              </div>
              {r.deadline && <Badge tone="neutral">À payer avant le {formatDate(r.deadline)}</Badge>}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
